import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { DeleteOutlined, Close, LinkOffOutlined } from '@mui/icons-material';
import { FullTable as Table } from '../../../components';


export interface ConfirmDeleteModalProps<T extends object> {
  /** Whether dialog is open */
  open: boolean;
  /** Main title (e.g. “Cannot delete application”) */
  title: string;
  /** Optional subtitle (e.g. the specific name) */
  subtitle?: string;
  /** Optional explanatory message */
  message?: string;
  /** react-table column definitions */
  columns: any[];
  /** The rows to show */
  data: T[];
  /** Loading flag */
  isLoading?: boolean;
  /** Close handler */
  onClose: () => void;
  /** Final “confirm” (e.g. delete the parent entity) */
  onConfirm?: () => void;
  /** Delete a single row */
  onDeleteItem?: (item: T) => void;
  /** Unlink a single row (clear out its foreign-key) */
  onUnlinkItem?: (item: T) => void;
  /** Bulk delete handler */
  onDeleteAllItems?: () => void;
  /** Text for the final confirm button */
  confirmText?: string;
  /** Text for the bulk delete button */
  deleteAllText?: string;
}

export function ConfirmDeleteModal<T extends object>({
  open,
  title,
  subtitle,
  message,
  columns,
  data,
  isLoading = false,
  onClose,
  onConfirm,
  onDeleteItem,
  onUnlinkItem,
  onDeleteAllItems,
  confirmText = 'Confirm',
  deleteAllText = 'Delete All'
}: ConfirmDeleteModalProps<T>) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const tableColumns = [...columns];
  if (onDeleteItem || onUnlinkItem) {
    tableColumns.push({
      id: 'actions',
      Header: '',
      Cell: ({ row }: { row: { original: T } }) => (
        <Box display="flex" gap={0.5}>
          {onUnlinkItem && (
            <IconButton
              size="small"
              color="primary"
              onClick={() => onUnlinkItem(row.original)}
            >
              <LinkOffOutlined fontSize="small" />
            </IconButton>
          )}
          {onDeleteItem && (
            <IconButton
              size="small"
              color="error"
              onClick={() => onDeleteItem(row.original)}
            >
              <DeleteOutlined fontSize="small" />
            </IconButton>
          )}
        </Box>
      )
    });
  }

  const hasRows = data.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
             <Typography variant="h6">{title}</Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
            <Close />
            </IconButton>
        </Box>
        </DialogTitle>

      <DialogContent dividers>
        {message && (
          <Box mb={0}>
            {message.split(/\.\s*/).map((line, idx) =>
              line.trim() ? (
                <Typography key={idx} variant="body1">
                  {line.trim()}
                </Typography>
              ) : null
            )}
          </Box>
        )}

        {/* bulk delete button */}
        {onDeleteAllItems && hasRows && (
          <Box mb={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteOutlined />}
              onClick={onDeleteAllItems}
            >
              {deleteAllText}
            </Button>
          </Box>
        )}

        {/* table */}
        <Box
          sx={{
            width: '100%',
            minWidth: '100%',
            overflow: 'auto',
            border: '1px solid #e0e0e0', 
            borderRadius: '2px',           
            background: 'white', 
            '& table': { width: '100%', tableLayout: 'fixed' },
            '& th:first-of-type, & td:first-of-type': {
              minWidth: '5% !important',
              width: '25% !important',
              maxWidth: '25% !important',
              paddingLeft: '10px !important',
              textAlign: 'left !important',
            
            },
            '& .MuiTableCell-root': {
              fontSize: '0.75rem',
              padding: '6px 8px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              borderBottom: '1px solid rgb(212, 210, 210)',
            },
            '& .MuiTableCell-head': {
              fontWeight: 'bold',
              borderBottom: '2px solid rgb(212, 210, 210)',
            }
          }}
        >
          <Table
            columns={tableColumns as any}
            data={data}
            isLoading={isLoading}
            serverPage={page}
            serverRecordCount={data.length}
            serverPerPage={rowsPerPage}
            handlePage={setPage}
            handleRowsPerPage={setRowsPerPage}
          />
        </Box>
      </DialogContent>

      {/* final confirm + cancel only when no rows remain */}
      {!hasRows && onConfirm && (
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={onClose} startIcon={<Close />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DeleteOutlined />}
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default ConfirmDeleteModal;
